import { apiFetch } from './apiFetch';

/**
 * Sign-on, from the browser's side.
 *
 * Note what is not here: no password, no token, no provider SDK. The browser
 * asks what kind of front door this deployment has, sends the person to it, and
 * afterwards holds nothing at all — the session is an httpOnly cookie the
 * JavaScript on this page cannot read, which is the point of it being httpOnly.
 * If this file ever grows a token in `localStorage`, that property is gone.
 */

export interface AuthConfig {
  mode: 'oidc' | 'demo';
  providerLabel: string;
  /** True only where the persona switcher is permitted — a development build. */
  canSwitchIdentity: boolean;
  /** The one sentence shown for every refusal. Served so there is one of it. */
  refusalMessage: string;
}

export interface SessionInfo {
  personId: string;
  displayName: string;
  roleLabel: string;
  scopeLabel: string;
  scopeType: string;
  isNarrowed: boolean;
  permissions: string[];
  authMethod: string;
  expiresAt: string | null;
  absoluteExpiresAt: string | null;
  sessions: Array<{
    id: string;
    authMethod: string;
    issuer: string | null;
    createdAt: string;
    lastSeenAt: string;
    expiresAt: string;
    absoluteExpiresAt: string;
  }>;
}

export function createHttpAuthRepository(baseUrl: string) {
  return {
    async config(): Promise<AuthConfig | null> {
      const response = await apiFetch(`${baseUrl}/auth/config`, { anonymous: true });
      if (!response.ok) return null;
      const body = (await response.json()) as { data: AuthConfig };
      return body.data;
    },

    /** Null when nobody is signed in. A 401 here is an answer, not an error. */
    async session(): Promise<SessionInfo | null> {
      const response = await apiFetch(`${baseUrl}/auth/session`, { anonymous: true });
      if (!response.ok) return null;
      const body = (await response.json()) as { data: SessionInfo };
      return body.data;
    },

    /** Development only. The server refuses this outright where a provider is configured. */
    async signInAs(person: string): Promise<boolean> {
      const response = await apiFetch(`${baseUrl}/auth/demo-session`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ person }),
        anonymous: true,
      });
      return response.ok;
    },

    async signOut(): Promise<void> {
      await apiFetch(`${baseUrl}/auth/logout`, { method: 'POST', anonymous: true });
    },

    /** Where the browser goes to meet the provider. A navigation, not a fetch. */
    loginUrl(returnTo: string): string {
      return `${baseUrl}/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
    },
  };
}
